/* StageFilterBar — per-user content filtering.

   Shown above any catalog list. Reads the user's `lexi_stage` from auth and:
     · By default → filters list to roles/specs relevant to that stage
     · "Show everything" button → reveals the full library (opt-in)
   Persists the choice in sessionStorage so toggling once carries across pages.

   Props:
     stage         — current user stage string (e.g. "final-year")
     showAll       — controlled boolean
     setShowAll    — setter
     contextLabel  — short label like "future-ready roles" or "specializations"
*/
import React from 'react';
import { Filter, Eye, EyeOff, Sparkles } from 'lucide-react';

const STAGE_LABEL = {
  'after-10th':  'After 10th',
  'after-inter': 'After Inter',
  'college':     'In college',
  'final-year':  'Final year / fresher',
  'working':     'Working',
  'parent':      'Parent',
};

const StageFilterBar = ({ stage, showAll, setShowAll, contextLabel = 'paths' }) => {
  if (!stage) return null;

  return (
    <div
      className="my-5 flex items-center justify-between gap-3 flex-wrap bg-white border border-gray-200 rounded-2xl px-4 py-2.5"
      data-testid="stage-filter-bar"
    >
      <span className="inline-flex items-center gap-2 text-sm text-gray-700 min-w-0">
        <Filter size={14} className="text-teal-600 shrink-0" />
        {showAll ? (
          <span><strong>Showing everything</strong> · all {contextLabel}, not just those tagged for your stage</span>
        ) : (
          <span>
            Showing <strong>{contextLabel} for {STAGE_LABEL[stage] || stage}</strong> — your stage.
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => setShowAll(!showAll)}
        data-testid="stage-filter-toggle"
        className={`inline-flex items-center gap-1.5 text-xs font-extrabold px-3 py-1.5 rounded-full transition-colors ${
          showAll
            ? 'bg-amber-100 text-amber-900 hover:bg-amber-200'
            : 'bg-teal-100 text-teal-800 hover:bg-teal-200'
        }`}
      >
        {showAll ? <><EyeOff size={12} /> Show only my stage</> : <><Eye size={12} /> Show everything</>}
      </button>
    </div>
  );
};

/* Helper — decide if a record matches the user's stage.
   Conservative defaults — if we can't classify confidently, we include it. */
export const matchesStage = (record, stage) => {
  if (!stage) return true;
  // Records can hint via these fields (no-op if absent)
  const hint = (
    (record.recommended_stages || record.stages || record.tags || record.audience || []).map((s) =>
      String(s).toLowerCase()
    )
  );
  if (hint.length) {
    return hint.some((h) => h.includes(stage)) || hint.includes('all');
  }
  return true;
};

/* useStageFilter — hook that persists the showAll toggle across pages */
export const useStageFilter = () => {
  const [showAll, setShowAllState] = React.useState(() => {
    try { return window.sessionStorage.getItem('lxt_show_all_v1') === '1'; }
    catch (_) { return false; }
  });
  const setShowAll = (v) => {
    setShowAllState(v);
    try { window.sessionStorage.setItem('lxt_show_all_v1', v ? '1' : '0'); } catch (_) { /* noop */ }
  };
  return { showAll, setShowAll };
};

export default StageFilterBar;
